"use client";

import React from "react";
import { MapPin, Phone, Mail, Linkedin, Twitter, Facebook, Loader, Building2, Handshake, Globe } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function PartnerForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="37540" className="flex flex-col items-center justify-center py-12 text-center space-y-4 animate-in fade-in duration-500">
        <div className="size-16 rounded-full bg-secondary/20 flex items-center justify-center text-secondary mb-2">
          <Handshake className="size-8" />
        </div>
        <h3 className="text-2xl font-semibold text-foreground">Message Received</h3>
        <p className="text-muted-foreground max-w-md mx-auto">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="mt-6 border-secondary text-secondary hover:bg-secondary/10">
          Send Another Message
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="full_name" rules={{
        required: "Full name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/80">Your Full Name</FormLabel>
              <FormControl>
                <Input placeholder="Jane Doe" className="border-border focus-visible:ring-secondary" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="company_name" rules={{
        required: "Organization name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/80">Organization (if applicable)</FormLabel>
              <FormControl>
                <Input placeholder="Organization Name" className="border-border focus-visible:ring-secondary" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/80">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="jane@example.com" className="border-border focus-visible:ring-secondary" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/80">Phone Number <span className="text-muted-foreground text-xs font-normal">(Optional)</span></FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+44 7000 000000" className="border-border focus-visible:ring-secondary" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="partnership_type" rules={{
      required: "Please select an inquiry type"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground/80">Nature of Inquiry</FormLabel>
            <Select onValueChange={field.onChange} defaultValue={field.value}>
              <FormControl>
                <SelectTrigger className="border-border focus:ring-secondary">
                  <SelectValue placeholder="Select an inquiry type" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                <SelectItem value="General Inquiry">General Inquiry</SelectItem>
                <SelectItem value="Donation Support">Donation Support</SelectItem>
                <SelectItem value="Volunteering">Volunteering</SelectItem>
                <SelectItem value="Partnership Proposal">Partnership Proposal</SelectItem>
                <SelectItem value="Other">Other</SelectItem>
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground/80">How can we help you? <span className="text-muted-foreground text-xs font-normal">(Optional)</span></FormLabel>
            <FormControl>
              <Textarea placeholder="Share your thoughts or questions with us..." className="min-h-[120px] resize-y border-border focus-visible:ring-secondary" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md text-destructive text-sm">
          {submitError}
        </div>}

      <Button type="submit" className="w-full md:w-auto bg-secondary text-secondary-foreground hover:bg-secondary/90 shadow-md transition-all duration-300" disabled={isSubmitting}>
        {isSubmitting ? <>
            <Loader className="mr-2 size-4 animate-spin" />
            Sending...
          </> : "Send Message"}
      </Button>
    </Form>;
}
export default function PartnerWithBoost() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section id="reach-our-team" className="bg-background text-foreground relative overflow-hidden ![padding:30px_0px_30px_0px]">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute -top-[20%] -left-[10%] w-[50%] h-[50%] bg-secondary/5 rounded-full blur-3xl" />
        <div className="absolute top-[40%] -right-[10%] w-[40%] h-[40%] bg-primary/5 rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          {}
          <div ref={ref} className={`space-y-10 transition-all duration-700 delay-100 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-secondary/10 text-secondary text-sm font-semibold tracking-wide">
                <Building2 className="size-4" />
                <span>UK Registered Charity: 1214126</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-primary">
                Reach Our Team
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed">
                We welcome inquiries from donors, volunteers, and community partners. Your interest helps us continue our mission of transforming lives through education in Ghana.
              </p>
            </div>

            <div className="space-y-8">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 size-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                  <MapPin className="size-5" />
                </div>
                <div>
                  <h4 className="font-bold text-primary mb-1">Office</h4>
                  <p className="text-muted-foreground">7 Willis Avenue, Sutton</p>
                  <p className="text-muted-foreground text-sm mt-1">Surrey, SM2 5HS, United Kingdom</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 size-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                  <Mail className="size-5" />
                </div>
                <div>
                  <h4 className="font-bold text-primary mb-1">Email Us</h4>
                  <Link to="mailto:info@farquharfisherfoundation.com" className="text-muted-foreground hover:text-secondary transition-colors">info@farquharfisherfoundation.co.uk</Link>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 size-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                  <Phone className="size-5" />
                </div>
                <div>
                  <h4 className="font-bold text-primary mb-1">Call Us</h4>
                  <Link to="tel:+447455323959" className="text-muted-foreground hover:text-secondary transition-colors">
                    +44 7455 323959
                  </Link>
                </div>
              </div>
            </div>

            {}
            <div className="relative w-full h-64 rounded-xl overflow-hidden border border-border/50 shadow-md group">
              
              {}
              
              
              {}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center gap-2">
                <div className="relative">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-secondary opacity-75"></span>
                  <div className="relative size-4 bg-secondary rounded-full border-2 border-background shadow-lg" />
                </div>
                <div className="bg-card/90 backdrop-blur-sm px-3 py-1.5 rounded-md shadow-sm border border-border/50 text-xs font-bold text-primary">
                  Foundation Office
                </div>
              </div>
              
              {}
              <div className="absolute inset-0 bg-gradient-to-t from-background/40 to-transparent pointer-events-none" />
            </div>

            {}
            <div className="flex items-center gap-4 pt-2">
              <Link to="#" className="size-10 rounded-full bg-muted flex items-center justify-center text-muted-foreground hover:bg-secondary hover:text-secondary-foreground transition-all duration-300" aria-label="LinkedIn">
                <Linkedin className="size-5" />
              </Link>
              <Link to="#" className="size-10 rounded-full bg-muted flex items-center justify-center text-muted-foreground hover:bg-secondary hover:text-secondary-foreground transition-all duration-300" aria-label="Twitter">
                <Twitter className="size-5" />
              </Link>
              <Link to="#" className="size-10 rounded-full bg-muted flex items-center justify-center text-muted-foreground hover:bg-secondary hover:text-secondary-foreground transition-all duration-300" aria-label="Facebook">
                <Facebook className="size-5" />
              </Link>
              <Link to="https://farquharfisherfoundation.co.uk" newTab className="size-10 rounded-full bg-muted flex items-center justify-center text-muted-foreground hover:bg-secondary hover:text-secondary-foreground transition-all duration-300" aria-label="Website">
                <Globe className="size-5" />
              </Link>
            </div>
          </div>

          {}
          <div className={`transition-all duration-700 delay-300 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <Card className="shadow-2xl border-border/60 bg-card/95 backdrop-blur-md rounded-xl">
              <CardHeader className="pb-6 border-b border-border/40">
                <CardTitle className="text-2xl font-bold text-primary">Send us a Message</CardTitle>
                <CardDescription className="text-muted-foreground">
                  Our team will respond to your inquiry as soon as possible. Thank you for your interest in our mission.
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-8">
                <FormProvider formKey="contact_form_contact" formId="contact_form_contact" sectionName="reach-our-team" defaultValues={{
                full_name: "",
                company_name: "",
                email: "",
                phone: "",
                partnership_type: "",
                message: ""
              }} validationRules={{
                full_name: {
                  required: true
                },
                company_name: {
                  required: true
                },
                email: {
                  required: true,
                  pattern: "email"
                },
                partnership_type: {
                  required: true
                }
              }} submitText="Send Message" successMessage="Thank you for reaching out to the Farquhar Fisher Foundation. We have received your message and will be in touch shortly." errorMessage="We encountered an issue submitting your request. Please try again later.">
                  <PartnerForm />
                </FormProvider>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>;
}